import React, { useState } from 'react';
import Modal from 'react-modal';
import { 
  ModalContent, Field, Label, EditableInput, PhoneInputContainer, 
  PhonePrefix, PhoneInput, NotesInput, ButtonRow, HeaderRow, 
  Button, DateTimeRow, ErrorMessage, modalStyles, dateTimeInputStyles,
  AddEmail, AddCompany, AddName, AddPhone, AddNotes, AddFollowupOn
} from '../../constants/leadModalStyles';
import { LeadStatus } from '../../constants/leadStatus';
import LeadService from '../../services/LeadService';
// Add Google Fonts import for Inria Serif
const fontLink = document.createElement('link');
fontLink.href = 'https://fonts.googleapis.com/css2?family=Inria+Serif:wght@400;700&display=swap';
fontLink.rel = 'stylesheet';
document.head.appendChild(fontLink);


const AddLeadModal = ({ isOpen, onRequestClose, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    followupDate: '',
    followupTime: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Validate 10 digits for Indian phone numbers
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear page error when user starts typing
    if (pageError) {
      setPageError('');
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits and limit to 10 characters
    if (/^\d*$/.test(value) && value.length <= 10) {
      handleInputChange('phone', value);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setPageError(''); // Clear any previous errors

    const leadData = {
      firstName: formData.name.split(' ')[0] || formData.name,
      lastName: formData.name.split(' ').slice(1).join(' ') || 'N.A',
      whatsAppNo: formData.phone.replace('+91', ''),
      email: formData.email,
      countryCode: '+91',
      createdOn: new Date().toISOString(),
      businessDetails: formData.company,
      company: formData.company,
      leadsStatus: LeadStatus.NEW_LEADS,
      notes: formData.notes,
    };

    // Add follow-up date if provided
    if (formData.followupDate && formData.followupTime) {
      const dateObj = new Date(`${formData.followupDate}T${formData.followupTime}`);
      leadData.followupDate = dateObj.toISOString();
      leadData.contactOn = dateObj.toISOString();
    }

    try {
      const response = await LeadService.addLead(leadData);
      if (response.data.success) {
        onSave(leadData);
        handleReset();
        onRequestClose();
      }

    } catch (error) {
      console.error('Error adding lead:', error);
      
      // Handle different types of errors
      if (error.response) {
        const errorMessage = error.response.data?.message || 
                           error.response.data?.error || 
                           'Failed to add lead. Please try again.';
        setPageError(errorMessage);
      } else if (error.request) {
        setPageError('Network error. Please check your connection and try again.');
      } else {
        setPageError('An unexpected error occurred. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    handleReset();
    onCancel();
  };

  const handleReset = () => {
    setFormData({
      name: '',
      company: '',
      email: '',
      phone: '',
      followupDate: '',
      followupTime: '',
      notes: ''
    });
    setErrors({});
    setPageError('');
  };

  const customStyles = modalStyles;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Add Lead"
      style={customStyles}
    >
      <ModalContent>
        <HeaderRow>
          <h4>Add Leads</h4>
        </HeaderRow>

        {pageError && (
          <div style={{
            backgroundColor: '#fee',
            color: '#c33',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
            border: '1px solid #fcc'
          }}>
            {pageError}
          </div>
        )}

        <Field>
          <Label>Name:</Label>
          <EditableInput
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={errors.name ? 'error' : ''}
            placeholder={AddName}
          />
          {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
        </Field>
        
        <Field>
          <Label>Company:</Label>
          <EditableInput
            type="text"
            value={formData.company}
            onChange={(e) => handleInputChange('company', e.target.value)}
            className={errors.company ? 'error' : ''}
            placeholder={AddCompany}
          />
          {errors.company && <ErrorMessage>{errors.company}</ErrorMessage>}
        </Field>
        
        <Field>
          <Label>Email:</Label>
          <EditableInput
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={errors.email ? 'error' : ''}
            placeholder={AddEmail}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </Field>
        
        <Field>
          <Label>Phone:</Label>
          <PhoneInputContainer className={errors.phone ? 'error' : ''}>
            <PhonePrefix>+91</PhonePrefix>
            <PhoneInput
              type="tel"
              value={formData.phone}
              onChange={handlePhoneChange}
              placeholder={AddPhone}
              maxLength={10}
            />
          </PhoneInputContainer>
          {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
        </Field>
        
        <DateTimeRow>
          <Label style={{ marginBottom: 0 }}>{AddFollowupOn}</Label>
          <input
            type="date"
            value={formData.followupDate}
            onChange={e => handleInputChange('followupDate', e.target.value)}
            style={dateTimeInputStyles}
          />
          <input
            type="time"
            value={formData.followupTime}
            onChange={e => handleInputChange('followupTime', e.target.value)}
            style={dateTimeInputStyles}
          />
        </DateTimeRow>
        
        <Field>
          <Label>Notes:</Label>
          <NotesInput
            value={formData.notes}
            onChange={e => handleInputChange('notes', e.target.value)}
            placeholder={AddNotes}
          />
        </Field>
        
        <ButtonRow>
          <Button data-variant="cancel" onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleSave}>Save</Button>
        </ButtonRow>
      </ModalContent>
    </Modal>
  );
};

export default AddLeadModal;
